import { useReadOnly } from 'utils/lifecycle'
import React, { useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import useSWR from 'swr';
import api from 'utils/api';
import { getApiErrorMessage } from 'utils/apiErrors';
import {
  Box, Button, Grid, Tab, Tabs, Typography,
  Pagination, CircularProgress, Alert
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import KaiyleeWorkspaceWindow from './components/KaiyleeWindows/KaiyleeWorkspaceWindow';
import BusinessPage from '../../components/BusinessPage';
import OfferWizard from './components/OfferWizard';
import OfferCard from './components/OfferCard';
import OfferDraftCard from './components/OfferDraftCard';
import { filterVisibleDrafts } from './drafts';
import {
  fetchOfferCounts,
  normalizeOfferCountRefreshTargets,
} from './offerCountRefresh';

const offerTypes = {
  active: 'active',
  scheduled: 'scheduled',
  paused: 'paused',
  draft: 'draft',
  expired: 'expired',
  archived: 'archived',
};

const OFFERS_PER_PAGE = 6;

const emptyKaiyleeDraft = {
  title: '',
  category: [],
  keywords: [],
  startDate: '',
  endDate: '',
  budget: '',
  claims: '',
  audience: '',
  description: '',
  redemption: '',
};

const Offers = () => {
  const readOnly = useReadOnly()

  const businessId = useSelector(state => state.business.businessId);
  const { enqueueSnackbar } = useSnackbar();
  const [currentTab, setCurrentTab] = useState(offerTypes.active);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isKaiyleeOpen, setIsKaiyleeOpen] = useState(false);
  const [isKaiyleeMinimized, setIsKaiyleeMinimized] = useState(false);
  const [configureDraft, setConfigureDraft] = useState(null);
  const [kaiyleeDraft, setKaiyleeDraft] = useState(emptyKaiyleeDraft);
  const [counts, setCounts] = useState(
    Object.fromEntries(Object.keys(offerTypes).map(k => [k, 0]))
  );

  const isDraftTab = currentTab === offerTypes.draft;

  const offersApiRoute = !isDraftTab
    ? `/offer/business/${businessId}?status=${currentTab.toUpperCase()}&size=${OFFERS_PER_PAGE}&page=${currentPage - 1}`
    : null;

  const draftsApiRoute = isDraftTab
    ? `/drafts/business/${businessId}/page?size=${OFFERS_PER_PAGE}&page=${currentPage - 1}`
    : null;

  const {
    data: offers,
    error: offersError,
    mutate: revalidateOffers,
  } = useSWR(
    offersApiRoute,
    () => api.get(offersApiRoute),
    { revalidateOnFocus: false }
  );

  const {
    data: drafts,
    error: draftsError,
    mutate: revalidateDrafts,
  } = useSWR(
    draftsApiRoute,
    () => api.get(draftsApiRoute),
    { revalidateOnFocus: false }
  );

  const fetchCounts = useCallback(async (targets) => {
    const countsData = await fetchOfferCounts({
      businessId,
      fetcher: api.get,
      targets,
    });

    if (Object.keys(countsData).length > 0) {
      setCounts(previous => ({ ...previous, ...countsData }));
    }
  }, [businessId]);

  useEffect(() => {
    if (businessId) {
      fetchCounts();
    }
  }, [businessId, fetchCounts]);

  const refreshOfferData = useCallback(async (targets) => {
    const statuses = normalizeOfferCountRefreshTargets(targets);
    if (statuses.length === 0) return;

    const currentTabIsAffected = statuses.includes(currentTab);
    const backgroundStatuses = statuses.filter(status => status !== currentTab);
    const refreshes = [];

    if (backgroundStatuses.length > 0) {
      refreshes.push(fetchCounts(backgroundStatuses));
    }

    if (currentTabIsAffected) {
      const refreshCurrentTab = (isDraftTab ? revalidateDrafts() : revalidateOffers())
        .then((refreshedPage) => {
          if (Number.isFinite(refreshedPage?.totalElements)) {
            setCounts(previous => ({
              ...previous,
              [currentTab]: refreshedPage.totalElements,
            }));
          }
        });

      refreshes.push(refreshCurrentTab.catch(() => {}));
    }

    await Promise.all(refreshes);
  }, [currentTab, fetchCounts, isDraftTab, revalidateDrafts, revalidateOffers]);

  useEffect(() => {
    const pageData = isDraftTab ? drafts : offers;
    const lastPage = Math.max(1, pageData?.totalPages || 0);
    if (pageData && currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [currentPage, drafts, isDraftTab, offers]);

  const handleTabChange = (_, newTab) => {
    setCurrentTab(newTab);
    setCurrentPage(1);
  };

  const getModalOffer = () => {
    if (typeof isModalOpen !== 'number') return undefined;
    if (isDraftTab) {
      return configureDraft?.draftId === isModalOpen
        ? configureDraft
        : drafts?.content?.find(d => d.draftId === isModalOpen);
    } else {
      return offers?.content?.find(o => o.offerDetails.offerId === isModalOpen);
    }
  };


  return (
    <BusinessPage title="Offers">
      <KaiyleeWorkspaceWindow
        open={isKaiyleeOpen}
        minimized={isKaiyleeMinimized}
        onClose={() => {
          setIsKaiyleeOpen(false);
          setIsKaiyleeMinimized(false);
        }}
        onMinimize={() => setIsKaiyleeMinimized(true)}
        onRestore={() => setIsKaiyleeMinimized(false)}
        draft={kaiyleeDraft}
        onDraftChange={setKaiyleeDraft}
        businessId={businessId}
        onOfferDataRefresh={refreshOfferData}
        onConfigureOffer={async (draftOfferId) => {
          if (!draftOfferId) return;
          let refreshedDrafts;
          try {
            refreshedDrafts = filterVisibleDrafts(
              await api.get(`/drafts/business/${businessId}`)
            );
          } catch (error) {
            enqueueSnackbar(
              getApiErrorMessage(error, 'Couldn’t load this draft. Please try again.'),
              { variant: 'error' }
            );
            return;
          }
          const targetDraft = refreshedDrafts.find(
            draft => Number(draft.draftId) === Number(draftOfferId)
          );
          if (!targetDraft) {
            enqueueSnackbar('This draft is no longer available.', { variant: 'error' });
            return;
          }
          setConfigureDraft(targetDraft);
          setCurrentTab(offerTypes.draft);
          setCurrentPage(1);
          setIsKaiyleeOpen(true);
          setIsKaiyleeMinimized(true);
          setIsModalOpen(Number(draftOfferId));
        }}
      />

      <Box sx={{ px: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Tooltip title="Kaiylee can help you create an offer" arrow>
            <Button disabled={readOnly}
              variant="contained"
              disableElevation
              onClick={() => {
                setIsKaiyleeOpen(true);
                setIsKaiyleeMinimized(false);
                setKaiyleeDraft(emptyKaiyleeDraft);
              }}
              sx={{
                borderRadius: '999px',
                px: 2.25,
                py: 0.9,
                minHeight: 40,
                bgcolor: 'secondary.main',
                color: '#000',
                '&:hover': {
                  bgcolor: 'secondary.main',
                  filter: 'brightness(0.95)',
                },
              }}
            >
              <AutoAwesomeRoundedIcon sx={{ fontSize: 18, color: '#fff', mr: 0.75 }} />
              Ask Kaiylee
              <Box
                component="sup"
                sx={{ ml: '1px', fontSize: '0.72em', lineHeight: 1, position: 'relative', top: '-0.28em' }}
              >
                ™
              </Box>
            </Button>
          </Tooltip>
          <Button disabled={readOnly}
            variant="contained"
            color="primary"
            onClick={() => setIsModalOpen(true)}
          >
            New {isDraftTab ? 'Offer' : 'Offer'}
          </Button>
        </Box>
      </Box>

      <Tabs
        value={currentTab}
        onChange={handleTabChange}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {Object.entries(offerTypes).map(([key, label]) => (
          <Tab
            key={key}
            value={key}
            label={`${label.charAt(0).toUpperCase() + label.slice(1)} (${counts[key] || 0})`}
          />
        ))}
      </Tabs>

      <Box sx={{ p: 2 }}>
        {isDraftTab ? (
          <>
            {draftsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                We couldn’t load drafts. Refresh the page and try again.
              </Alert>
            )}
            {!drafts && !draftsError && (
              <Box display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            )}
            {drafts?.content?.length === 0 && (
              <Typography>No drafts found</Typography>
            )}
            <Grid container spacing={2}>
              {drafts?.content?.map(draft => (
                <Grid item xs={12} key={draft.draftId}>
                  <OfferDraftCard
                    draft={draft}
                    editDraft={() => {
                      setConfigureDraft(null);
                      setIsModalOpen(draft.draftId);
                    }}
                    onOfferDataRefresh={refreshOfferData}
                  />
                </Grid>
              ))}
            </Grid>
            {drafts?.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                <Pagination
                  count={drafts.totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  color="primary"
                />
              </Box>
            )}
          </>
        ) : (
          <>
            {offersError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                We couldn’t load offers. Refresh the page and try again.
              </Alert>
            )}
            {!offers && !offersError && (
              <Box display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            )}
            {offers?.content?.length === 0 && (
              <Typography>No {currentTab} offers found</Typography>
            )}
            <Grid container spacing={2}>
              {offers?.content?.map(offer => (
                <Grid item xs={12} key={offer.offerDetails.offerId}>
            <OfferCard
              offer={offer.offerDetails}
              clicks={offer.clicks}
              impressions={offer.impressions}
              spend={offer.spent}
              editOffer={() => setIsModalOpen(offer.offerDetails.offerId)}
              offerType={currentTab}
              offerTypes={offerTypes}
              onOfferDataRefresh={refreshOfferData}
              onCloneDraft={draft => {
                setConfigureDraft(draft);
                setCurrentTab(offerTypes.draft);
                setCurrentPage(1);
                setIsModalOpen(draft.draftId);
              }}
            />
                </Grid>
              ))}
            </Grid>
            {offers?.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                <Pagination
                  count={offers.totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {isModalOpen && (
        <OfferWizard
          offer={getModalOffer()}
          isDraft={isDraftTab}
          onClose={() => {
            setIsModalOpen(false);
            setConfigureDraft(null);
          }}
          onOfferDataRefresh={refreshOfferData}
          onOfferUpdated={(response) => refreshOfferData([
            currentTab,
            response?.status,
          ])}
        />
      )}
    </BusinessPage>
  );
};

export default Offers;
